import React, { useState } from 'react';
import { RankingData, removeKeyword, trackKeyword } from '@/api/seoApi';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Trash2,
  CheckCircle2,
  HelpCircle,
  FileText,
  RefreshCw,
} from 'lucide-react';

interface KeywordRankingsTableProps {
  rankings: RankingData[];
  siteId: string;
  onRefresh: () => void;
}

export function KeywordRankingsTable({ rankings, siteId, onRefresh }: KeywordRankingsTableProps) {
  const [sortBy, setSortBy] = useState<'position' | 'change'>('position');
  const [removing, setRemoving] = useState<string | null>(null);
  const [tracking, setTracking] = useState<string | null>(null);

  const handleRemove = async (keywordId: string) => {
    if (!confirm('Are you sure you want to stop tracking this keyword?')) {
      return;
    }

    setRemoving(keywordId);
    try {
      await removeKeyword(siteId, keywordId);
      onRefresh();
    } catch (error) {
      console.error('Failed to remove keyword:', error);
      alert('Failed to remove keyword');
    } finally {
      setRemoving(null);
    }
  };

  const handleTrackNow = async (keywordId: string) => {
    setTracking(keywordId);
    try {
      await trackKeyword(siteId, keywordId);
      onRefresh();
    } catch (error) {
      console.error('Failed to track keyword:', error);
      alert('Failed to track keyword');
    } finally {
      setTracking(null);
    }
  };

  const sortedRankings = [...rankings].sort((a, b) => {
    if (sortBy === 'position') {
      const posA = a.current_position || 999;
      const posB = b.current_position || 999;
      return posA - posB;
    } else {
      const changeA = a.position_change || 0;
      const changeB = b.position_change || 0;
      return changeB - changeA; // Descending (biggest improvements first)
    }
  });

  const renderPositionBadge = (position: number | null) => {
    if (!position) {
      return <Badge variant="secondary">Not ranked</Badge>;
    }

    if (position <= 3) {
      return <Badge className="bg-green-500 hover:bg-green-600">{position}</Badge>;
    }

    if (position <= 10) {
      return <Badge className="bg-blue-500 hover:bg-blue-600">{position}</Badge>;
    }

    if (position <= 20) {
      return <Badge className="bg-yellow-500 hover:bg-yellow-600">{position}</Badge>;
    }

    return <Badge variant="outline">{position}</Badge>;
  };

  const renderChangeIndicator = (change: number | null) => {
    if (!change || change === 0) {
      return (
        <div className="flex items-center text-muted-foreground">
          <Minus className="h-4 w-4 mr-1" />
          <span className="text-sm">-</span>
        </div>
      );
    }

    if (change > 0) {
      return (
        <div className="flex items-center text-green-600">
          <TrendingUp className="h-4 w-4 mr-1" />
          <span className="text-sm font-medium">+{change}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center text-red-600">
        <TrendingDown className="h-4 w-4 mr-1" />
        <span className="text-sm font-medium">{change}</span>
      </div>
    );
  };

  const renderSerpFeatures = (ranking: RankingData) => {
    const features = [];

    if (ranking.has_featured_snippet) {
      features.push(
        <Badge key="fs" variant="outline" className="text-xs">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Featured Snippet
        </Badge>
      );
    }

    if (ranking.has_people_also_ask) {
      features.push(
        <Badge key="paa" variant="outline" className="text-xs">
          <HelpCircle className="h-3 w-3 mr-1" />
          PAA
        </Badge>
      );
    }

    if (ranking.has_knowledge_panel) {
      features.push(
        <Badge key="kp" variant="outline" className="text-xs">
          <FileText className="h-3 w-3 mr-1" />
          Knowledge Panel
        </Badge>
      );
    }

    return features.length > 0 ? (
      <div className="flex flex-wrap gap-1">{features}</div>
    ) : (
      <span className="text-muted-foreground text-sm">-</span>
    );
  };

  if (rankings.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No keywords tracked yet. Click "Add Keyword" to start tracking rankings.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={sortBy === 'position' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSortBy('position')}
        >
          Sort by Position
        </Button>
        <Button
          variant={sortBy === 'change' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSortBy('change')}
        >
          Sort by Change
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Keyword</TableHead>
            <TableHead>Position</TableHead>
            <TableHead>Change</TableHead>
            <TableHead>SERP Features</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Device</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRankings.map((ranking) => (
            <TableRow key={ranking.id}>
              <TableCell className="font-medium">{ranking.keyword}</TableCell>
              <TableCell>{renderPositionBadge(ranking.current_position)}</TableCell>
              <TableCell>{renderChangeIndicator(ranking.position_change)}</TableCell>
              <TableCell>{renderSerpFeatures(ranking)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {ranking.location}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground capitalize">
                {ranking.device}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleTrackNow(ranking.id)}
                    disabled={tracking === ranking.id}
                    title="Track this keyword now"
                  >
                    <RefreshCw className={`h-4 w-4 ${tracking === ranking.id ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(ranking.id)}
                    disabled={removing === ranking.id}
                    title="Stop tracking this keyword"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
